import {
    Fragment,
    FunctionComponent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    deleteMailLogo,
    getMailLog,
    postMailLogo,
} from '@/api/v2/mailConfiguration';
import { Box, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNotification } from '@starjack/features/notifications';
import { Dropzone } from '@/components/Dropzone/Dropzone';
import { ACCEPTED_FILES_IMAGE } from '@/constants';

export type LogoType = 'header' | 'footer';

type ImageUploadState = 'loading' | 'empty' | 'new' | 'existing';

const containerDimension = {
    width: 580,
    height: 435,
};

type LogoUploadContainerProps = {
    logoType: LogoType;
};

export const LogoUploadContainer: FunctionComponent<
    LogoUploadContainerProps
> = ({ logoType }) => {
    const [newLogo, setNewLogo] = useState<File>(null);
    const [existingLogo, setExistingLog] = useState<File>(null);
    const [imageUploadState, setImageUploadState] =
        useState<ImageUploadState>('loading');
    const { addNotification } = useNotification();
    const logoFetched = useRef(false);
    const objectUrl = useMemo(() => {
        const toUrl = newLogo || existingLogo || null;

        return toUrl ? URL.createObjectURL(toUrl) : '';
    }, [newLogo, existingLogo]);
    const [isLoading, setIsLoading] = useState(false);

    const onSaveButton = () => {
        if (newLogo) {
            setIsLoading(true);
            postMailLogo(logoType, newLogo).then((ok) => {
                if (ok) {
                    addNotification({
                        message: 'Logo erfolgreich gespeichert',
                        type: 'success',
                    });
                    setExistingLog(newLogo);
                    setNewLogo(null);
                    setImageUploadState('existing');
                    setIsLoading(false);
                }
            });
        }
    };

    const onDeleteLogo = () => {
        if (existingLogo) {
            setIsLoading(true);
            deleteMailLogo(logoType).then((ok) => {
                if (ok) {
                    setExistingLog(null);
                    setImageUploadState('empty');
                    addNotification({
                        message: 'Logo erfolgreich gelöscht',
                        type: 'success',
                    });
                    setIsLoading(false);
                }
            });
        }
    };

    useEffect(() => {
        if (!logoFetched.current) {
            logoFetched.current = true;
            getMailLog(logoType).then((data) => {
                if (data) {
                    setExistingLog(data);
                    setImageUploadState('existing');
                    return;
                }

                setImageUploadState('empty');
            });
        }
    }, []);

    return (
        <Fragment>
            {objectUrl ? (
                <img
                    src={objectUrl}
                    alt={`Logo ${logoType}`}
                    style={{
                        ...containerDimension,
                        objectFit: 'contain',
                    }}
                />
            ) : (
                <Box
                    sx={{
                        ...containerDimension,
                    }}
                >
                    <Dropzone
                        accept={ACCEPTED_FILES_IMAGE}
                        onDrop={async (acceptedFiles) => {
                            const [file] = acceptedFiles;

                            if (file) {
                                setNewLogo(file);
                                setImageUploadState('new');
                            }
                        }}
                    />
                </Box>
            )}

            <Stack
                spacing={2}
                sx={{
                    pt: 3,
                    width: '100%',
                }}
                direction='row'
            >
                {imageUploadState === 'new' && (
                    <Fragment>
                        <LoadingButton
                            onClick={onSaveButton}
                            variant='contained'
                            loading={isLoading}
                        >
                            Speichern
                        </LoadingButton>

                        <LoadingButton
                            onClick={() => {
                                setNewLogo(null);
                                setImageUploadState('empty');
                            }}
                            variant='contained'
                            color='error'
                        >
                            Auswahl löschen
                        </LoadingButton>
                    </Fragment>
                )}

                {imageUploadState === 'existing' && (
                    <LoadingButton
                        onClick={onDeleteLogo}
                        variant='contained'
                        color='error'
                        loading={isLoading}
                    >
                        Bestehendes Logo löschen
                    </LoadingButton>
                )}
            </Stack>
        </Fragment>
    );
};
